import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useNavigate } from "react-router-dom";

import { PageDirectoryEntryName } from "../../../api/PageDirectory";
import APButton from "../../../components/buttons/APButton";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import BackButton from "../../../components/buttons/BackButton";
import HomeButton from "../../../components/buttons/HomeButton";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import { isDossierActive } from "../../../helpers/dossiers";
import { useAuth } from "../../../providers/AuthProvider";
import { Dossier } from "../../../types/Dossier";

export type BackButtonType = "back" | "home";
export type NavButtonType =
  | "submit-claim"
  | "view-budgets"
  | "view-transactions"
  | "view-balances"
  | "update-bank";

const ActiveDossierNavButtons = ({
  dossier,
  backTypes = ["back"],
  navButtons = [],
  rightNavButtons = [],
}: {
  dossier: Dossier;
  backTypes: BackButtonType[];
  navButtons: NavButtonType[];
  rightNavButtons?: NavButtonType[];
}) => {
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();

  const renderNavButton = (buttonType: NavButtonType, index: number) => {
    let buttonText: string;
    let pageEntry: PageDirectoryEntryName;
    let externalOnly: boolean = false;
    let activeOnly: boolean = false;
    let queryParams: string | undefined;

    switch (buttonType) {
      case "submit-claim":
        buttonText = "New Claim";
        pageEntry = "createDossierExpenseClaim";
        externalOnly = true;
        activeOnly = true;
        break;
      case "view-budgets":
        buttonText = "Budget";
        pageEntry = "dossierBudgets";
        break;
      case "view-transactions":
        buttonText = "Transactions";
        pageEntry = "dossierTransactions";
        break;
      case "view-balances":
        buttonText = "Balance";
        pageEntry = "dossierBalances";
        break;
      case "update-bank":
        buttonText = "Bank";
        pageEntry = "userDirectDebit";
        externalOnly = true;
        activeOnly = true;
        queryParams = `?redirectOnSubmit=${location.pathname}`;
        break;
    }

    if (activeOnly && !isDossierActive(dossier)) {
      return null;
    }

    const button = (
      <APButton
        key={`${buttonType}-button-${index}`}
        text={buttonText}
        color="primary"
        variant="contained"
        onClick={() => {
          navigate(
            `${getScopedPageUrl(pageEntry, dossier.id)}${queryParams || ""}`
          );
        }}
      />
    );

    return externalOnly ? (
      <RestrictedAppScope
        key={`${buttonType}-${index}-wrapper`}
        scope="external"
      >
        {button}
      </RestrictedAppScope>
    ) : (
      button
    );
  };

  return (
    <Box
      sx={{
        pt: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <Stack
        spacing={2}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box display="flex" gap={1}>
            {backTypes.includes("back") && <BackButton />}
            {backTypes.includes("home") && (
              <HomeButton destination="dossiersHome" />
            )}
          </Box>
          {rightNavButtons && (
            <Box display="flex" gap={1}>
              {rightNavButtons.map(renderNavButton)}
            </Box>
          )}
        </Box>
        <Box display="flex" gap={1}>
          {navButtons.map(renderNavButton)}
        </Box>
        <RestrictedAppScope scope="external">
          <Box>
            <APContactHelpButton
              regardingOpportunityId={dossier?.opportunity?.id ?? undefined}
            />
          </Box>
        </RestrictedAppScope>
      </Stack>
    </Box>
  );
};

export default ActiveDossierNavButtons;
